import { useApolloClient } from '@apollo/client';
import { useEffect, useMemo } from 'react';
import { useMount } from 'react-use';

import { useApiClient } from '@eluve/api-client-provider';
import {
  ScheduleBackgroundTaskAction,
  useTaskProcessor,
} from '@eluve/appointment-tasks';
import { usePreventAutoReloadOnNewVersion } from '@eluve/blocks';
import { FCC } from '@eluve/components';
import { createCorrelatedLogger, useNamedLogger } from '@eluve/logger';
import { useUserIdFromSession } from '@eluve/session-helpers';

import { useDb } from './FilesDbProvider';
import {
  PeriodicRemoteFileSyncContext,
  usePeriodicRemoteFileSyncActor,
} from './hooks/usePeriodicRemoteFileSyncMachine';
import { makeDefaultRemoteFileSyncMachineLogic } from './remoteFileSyncFactory';
import { useUserFileSystemStore } from './user-file-system.store';

const DEFAULT_SYNC_INTERVAL_MS = 20 * 1000; // 20 seconds

type PeriodicRemoteFileSyncProviderProps = {
  tenantId: string;
  isOnline: boolean;
  syncIntervalMs?: number;
};

export const PeriodicRemoteFileSyncMachineProvider: FCC<
  PeriodicRemoteFileSyncProviderProps
> = ({
  children,
  tenantId,
  isOnline,
  syncIntervalMs = DEFAULT_SYNC_INTERVAL_MS,
}) => {
  const logger = useNamedLogger('PeriodicRemoteFileSyncProvider');
  const log = useMemo(() => createCorrelatedLogger(logger, {}).log, [logger]);
  const userId = useUserIdFromSession();

  const db = useDb();
  const apiClient = useApiClient();
  const apolloClient = useApolloClient();
  const userFileSystem = useUserFileSystemStore(
    (store) => store.userFileSystem,
  );
  const { taskProcessor } = useTaskProcessor();

  const scheduleBackgroundTask: ScheduleBackgroundTaskAction = (_, task) =>
    taskProcessor.addTask(task);

  useMount(() => {
    log('info', 'Mounting PeriodicRemoteFileSyncMachineProvider');
  });

  return (
    <PeriodicRemoteFileSyncContext.Provider
      options={{
        input: {
          tenantId,
          userId,
          syncIntervalMs,
          isOnline,
        },
      }}
      logic={makeDefaultRemoteFileSyncMachineLogic({
        apiClient,
        apolloClient,
        userFileSystem,
        db,
        log,
        scheduleBackgroundTask,
      })}
    >
      <OnlineStatusMachineSync isOnline={isOnline} />
      {children}
    </PeriodicRemoteFileSyncContext.Provider>
  );
};

const OnlineStatusMachineSync: React.FC<{
  isOnline: boolean;
}> = ({ isOnline }) => {
  const machine = usePeriodicRemoteFileSyncActor();

  // Prevent the browser from auto-reloading while an upload is in progress
  useEffect(
    function preventAutoReloadDuringUpload() {
      const REASON = 'file-syncing';
      const subscription = machine.subscribe((state) => {
        if (state.matches('WaitingForNextSync')) {
          usePreventAutoReloadOnNewVersion.getState().removeReason(REASON);
        } else {
          usePreventAutoReloadOnNewVersion.getState().addReason(REASON);
        }
      });

      return () => {
        subscription.unsubscribe();
        usePreventAutoReloadOnNewVersion.getState().removeReason(REASON);
      };
    },
    [machine],
  );

  useEffect(
    function syncOnlineStatusState() {
      machine.send({
        type: isOnline ? 'DEVICE_ONLINE' : 'DEVICE_OFFLINE',
      });
    },
    [isOnline, machine],
  );

  return null;
};
